import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@els/els-react--button';
import { Card } from '@els/els-react--card';
import { Loader } from '@els/els-react--loader';
import { Pill } from '@els/els-react--pill';
import { TabGroup, TabPanel } from '@els/els-react--tab-group';
import { Table } from '@els/els-react--table';
import { TogglePanel, TogglePanelCollection } from '@els/els-react--toggle-panel';
import { imageTypes } from '@Constants/rx-ui/constants';
import Footer from '@Components/Footer';
import NotifyDialogsService from '@Containers/Dialogs/NotifyDialogsService';
import history from '@AppRoutes/history';
import { searchReactionResultsByIds } from './ConditionsReactionResultsService';
import { getProjectRankFunctionalGroups } from './ConditionsService';
import ConditionsReactionImage from './components/ConditionsReactionImage';
import ConditionsSourceLink from './components/ConditionsSourceLink';
import ConditionsSummaryChart from './components/ConditionsSummaryChart';
import ConditionsExportModal from './components/ConditionsExportModal';
import ConditionsInfoTooltip from './components/ConditionsInfoTooltip';
import CONDITIONS_DETAIL_COLUMNS from './components/ConditionsDetailsTableColumns';
import ConditionsDetailsTableHeader from './components/ConditionsDetailsTableHeader';
import ConditionsDetailsTableRow from './components/ConditionsDetailsTableRow';
import { compareRank, conditionsSortFn } from './components/conditionsDetailSort';
import useConditionsFocusRestoration, { scheduleConditionsFocusRestoration } from './hooks/useConditionsFocusRestoration';
import {
  trackSummaryTopReagents,
  trackSummaryTopSolvents,
  trackSummaryHide,
  trackSimilarRank,
  trackSimilarSortMedianYield,
  trackSimilarSortNumberOfReactions,
  trackSimilarExamples,
  trackMyProjectsExport1,
} from './analytics';
import {
  CHART_CONFIGS,
  DEFAULT_SORT_BY,
  DEFAULT_SORT_ORDER,
  EMPTY_SUMMARY,
  HEADER_METRIC_CONFIGS,
  SIMILARITY_LEVEL_STYLES,
  SORTABLE_COLUMNS,
  SUMMARY_CARD_TOOLTIP,
  SUMMARY_TABS,
} from './components/conditionsDetailConstants';
import { CONDITIONS_PAGE_TITLE } from './constants';
import './ConditionsDetail.scss';

const idPropType = PropTypes.oneOfType([PropTypes.string, PropTypes.number]);
const CONDITION_SETS_PAGE_SIZE = 10;
const CONDITION_SETS_TABLE_ID = 'conditions-detail-condition-sets';
const COLLAPSED_CONDITION_SET_ANCHOR_ID = 'conditions-detail-collapsed-anchor-row';

function getSummaryTabData(summary, key, tab) {
  const bucket = summary?.[key];
  return Array.isArray(bucket?.[tab]) ? bucket[tab] : [];
}

function getMatchDistributionCount(summary, level) {
  const matchDistribution = Array.isArray(summary?.matchDistribution) ? summary.matchDistribution : [];
  const item = matchDistribution.find(({ name }) => String(name).toLowerCase() === String(level).toLowerCase());
  return item?.count ?? 0;
}

function getDefaultSummaryTabIndex(summary, key) {
  const firstPopulatedIndex = SUMMARY_TABS.findIndex((tab) => getSummaryTabData(summary, key, tab).length > 0);
  return firstPopulatedIndex === -1 ? 0 : firstPopulatedIndex;
}

function getDetailTitle(run = {}) {
  return run?.projectName || (run?.number ? `${CONDITIONS_PAGE_TITLE} result #${run.number}` : CONDITIONS_PAGE_TITLE);
}

// eslint-disable-next-line default-param-last
function getFunctionalGroupReactionIds(group = {}, type) {
  return type === 'reacted' ? group?.reactedReactionIds || [] : group?.unreactedReactionIds || [];
}

function findFunctionalGroup(groups = [], request = {}) {
  if (request?.fgId != null) {
    return groups.find((group) => String(group?.fgId) === String(request.fgId));
  }

  return groups.find((group) => group?.label === request?.label);
}

function SummaryCardHeading({ chart }) {
  return (
    <h3 className="conditions-detail__chart-heading">
      <span>{chart.heading}</span>
      <ConditionsInfoTooltip ariaLabel={`${chart.heading} information`} dataE2e={`conditions-summary-${chart.key}-tooltip`}>
        {SUMMARY_CARD_TOOLTIP}
      </ConditionsInfoTooltip>
    </h3>
  );
}

SummaryCardHeading.propTypes = {
  chart: PropTypes.shape({
    heading: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
};

function ConditionsSummaryTabGroup({ chart, summary, onBeforeNavigate = () => null }) {
  const tabGroupRef = useRef();
  const defaultTabIndex = getDefaultSummaryTabIndex(summary, chart.key);
  const summaryTabLabels = SUMMARY_TABS.map((tab) => {
    const tabLevel = tab.toLowerCase();
    const pillStyle = SIMILARITY_LEVEL_STYLES[tabLevel];

    return (
      <span key={tab} className="rx-summary-tab">
        <span>{tab}</span>
        <Pill
          pillColor={pillStyle?.pillColor}
          textColor={pillStyle?.textColor}
          className={`rx-summary-tab__pill rx-summary-tab__pill--${tabLevel}`}>
          {String(getMatchDistributionCount(summary, tab))}
        </Pill>
      </span>
    );
  });

  useEffect(() => {
    tabGroupRef.current?.setActiveTab(defaultTabIndex);
  }, [defaultTabIndex]);

  const handleTabChange = (index) => {
    const variant = SUMMARY_TABS[Number(index)]?.toLowerCase();
    if (!variant) return;
    if (chart.key === 'topReagents') {
      trackSummaryTopReagents(variant);
    } else if (chart.key === 'topSolvents') {
      trackSummaryTopSolvents(variant);
    }
  };

  return (
    <TabGroup
      ref={tabGroupRef}
      id={`rx-summary-tabs-${chart.key}`}
      className="rx-summary-tab-group"
      hasDivider
      tabs={summaryTabLabels}
      onTabChange={handleTabChange}>
      {SUMMARY_TABS.map((tab) => {
        const tabData = getSummaryTabData(summary, chart.key, tab);
        return (
          <TabPanel key={tab}>
            {tabData.length ? (
              <ConditionsSummaryChart title={`${chart.a11yTitle} — ${tab}`} data={tabData} onBeforeNavigate={onBeforeNavigate} />
            ) : (
              <p className="u-els-font-size-meta u-els-color-n5">No matching reactions at this similarity level.</p>
            )}
          </TabPanel>
        );
      })}
    </TabGroup>
  );
}

ConditionsSummaryTabGroup.propTypes = {
  chart: PropTypes.shape({
    a11yTitle: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  summary: PropTypes.shape({}).isRequired,
  onBeforeNavigate: PropTypes.func,
};

function ConditionsDetail({
  match,
  run = null,
  recommendations = [],
  summary = EMPTY_SUMMARY,
  isLoading = false,
  detailHasSettled = false,
  error = '',
  detailSortBy = DEFAULT_SORT_BY,
  detailSortOrder = DEFAULT_SORT_ORDER,
  detailRestoration = null,
  databaseId = undefined,
  fetchProject = () => null,
  setDetailSort = () => null,
  setDetailRestoration = () => null,
  consumeDetailRestoration = () => null,
  exportProject = () => null,
  requestExportDialog = async () => ({ canOpen: true }),
  isExportBusy = false,
  onProjectLoaded = () => null,
  setBlockingNavigation = () => null,
}) {
  const { id } = match.params;
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [exportAttempts, setExportAttempts] = useState({});
  const [visibleConditionSetCount, setVisibleConditionSetCount] = useState(CONDITION_SETS_PAGE_SIZE);
  const activeRun = run || {};

  const rememberAnchor = useConditionsFocusRestoration(setDetailRestoration);

  useEffect(() => {
    fetchProject(id);
  }, [fetchProject, id]);

  useEffect(() => {
    setVisibleConditionSetCount(CONDITION_SETS_PAGE_SIZE);
  }, [id]);

  useEffect(() => {
    if (run) {
      onProjectLoaded(getDetailTitle(run));
    }
  }, [onProjectLoaded, run]);

  useEffect(() => {
    if (!detailRestoration?.focusTargetId || !detailHasSettled || isLoading || !run) return undefined;

    return scheduleConditionsFocusRestoration(detailRestoration, consumeDetailRestoration);
    // detailRestoration is intentionally not a trigger: it is written before outbound navigation.
    // Restoration should run only after this page mounts and detail data is ready again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consumeDetailRestoration, detailHasSettled, isLoading, run]);

  const openReactionResults = async ({
    reactionIds,
    getReactionIds,
    focusTargetId,
    onTrack = () => null,
    errorTitle = 'Reaction results unavailable',
    errorText = 'Reaction results could not be opened.',
  }) => {
    if (!reactionIds?.length && !getReactionIds) return;
    onTrack();
    setBlockingNavigation(true);
    try {
      const resolvedReactionIds = reactionIds?.length ? reactionIds : await getReactionIds();

      if (!resolvedReactionIds?.length) {
        throw new Error('No reaction IDs were returned for this functional group.');
      }

      const { pathName, stateParams } = await searchReactionResultsByIds(resolvedReactionIds);
      setBlockingNavigation(false);
      rememberAnchor(focusTargetId);
      history.push({ pathname: pathName, state: stateParams });
    } catch (requestError) {
      setBlockingNavigation(false);
      Logger.error('ConditionsDetail open reaction results failed with error:', requestError);
      NotifyDialogsService.openModalWindow({
        type: 'error',
        title: errorTitle,
        text: requestError?.message || errorText,
        confirmText: 'Close',
      });
    }
  };

  const rememberAnchorAndBlockNavigation = (focusTargetId) => {
    rememberAnchor(focusTargetId);
    setBlockingNavigation(true);
  };

  // FR-3.6: open a reaction Results page built from every example RX.ID (no truncation).
  const openExamples = (reactionIds, focusTargetId) =>
    openReactionResults({
      reactionIds,
      focusTargetId,
      onTrack: trackSimilarExamples,
      errorTitle: 'Example reactions unavailable',
      errorText: 'Example reactions could not be opened.',
    });

  const getFunctionalGroupReactionIdsForSearch = async (request = {}) => {
    const immediateReactionIds = Array.isArray(request) ? request : request?.reactionIds;

    // TODO: Remove this shortcut after project detail responses no longer include functional-group reaction ID arrays.
    if (immediateReactionIds?.length) {
      return immediateReactionIds;
    }

    const groups = await getProjectRankFunctionalGroups(id, request?.rank);
    const group = findFunctionalGroup(groups, request);

    return getFunctionalGroupReactionIds(group, request?.type);
  };

  const openFunctionalGroupReactions = (request, focusTargetId) =>
    openReactionResults({
      getReactionIds: () => getFunctionalGroupReactionIdsForSearch(request),
      focusTargetId,
      errorTitle: 'Functional group reactions unavailable',
      errorText: 'Functional group reactions could not be opened.',
    });

  // Keep data as plain primitives / plain objects so lodash.cloneDeep (used internally
  // by Leyden Table's sort) does not recurse into React element trees. Rows are pre-sorted
  // by rank ascending so the table renders rank 1 first (FR-3.2) and blank-yield rows keep
  // rank-ascending as the secondary sort key (FR-3.3).
  const tableData = recommendations
    .map((item) => ({
      rank: item.rank,
      reagentsCatalysts: item.reagentsCatalysts ?? [],
      solvents: item.solvents ?? [],
      reactionCount: item.reactionCount,
      rcLevel: item.rcLevel,
      fgLability: item.fgLability,
      yieldPercent: item.yieldPercent,
      reactionIds: item.reactionIds ?? [],
    }))
    .sort((a, b) => compareRank(a.rank, b.rank));

  const renderReactionPreview = () => {
    const unavailableMessage = <p className="u-els-font-size-meta u-els-color-n5">Reaction structure preview unavailable</p>;
    return (
      <ConditionsReactionImage
        reaction={run?.reactionString}
        imageType={imageTypes.CONDITIONS_NAVIGATOR_L}
        className="conditions-detail__reaction-image"
        alt="Query reaction structure"
        loadingFallback={<Loader isVisible isBlocking={false} />}
        emptyFallback={unavailableMessage}
      />
    );
  };

  if (!detailHasSettled || isLoading) {
    return (
      <div className="conditions-detail">
        <main className="conditions-detail__main" id="main-content" aria-busy="true">
          <Loader isVisible isBlocking />
        </main>
      </div>
    );
  }

  if (error || !run) {
    return (
      <div className="conditions-detail">
        <main className="conditions-detail__main" id="main-content">
          <div role="alert">{error || 'Condition search was not found.'}</div>
        </main>
      </div>
    );
  }

  const headerMetrics = HEADER_METRIC_CONFIGS.map((metric) => ({
    ...metric,
    value: metric.summaryLevel ? getMatchDistributionCount(summary, metric.summaryLevel) : activeRun?.[metric.valueKey],
  }));
  const sortedTableData = [...tableData].sort((left, right) =>
    conditionsSortFn(left[detailSortBy], right[detailSortBy], detailSortOrder, detailSortBy)
  );
  const visibleTableData = sortedTableData.slice(0, visibleConditionSetCount);
  const hasConditionSetToggle = tableData.length > CONDITION_SETS_PAGE_SIZE;
  const isFullConditionSetListVisible = visibleConditionSetCount >= tableData.length;
  const conditionSetToggleLabel = isFullConditionSetListVisible ? 'Show less' : 'Show more';
  const conditionSetStatusMessage = `Showing ${visibleTableData.length} of ${tableData.length} condition sets.`;
  const handleDetailSortChange = (sortBy, sortOrder) => {
    if (sortBy === 'yieldPercent') {
      trackSimilarSortMedianYield();
    } else if (sortBy === 'reactionCount') {
      trackSimilarSortNumberOfReactions();
    } else if (sortBy === 'rank') {
      trackSimilarRank();
    }
    setDetailSort(id, sortBy, sortOrder);
  };
  const handleExport = ({ selectedFormat } = {}) => {
    if (!selectedFormat?.value) return;

    exportProject({
      projectId: id,
      docFormat: selectedFormat.value,
      databaseId,
      detailSortBy,
      detailSortOrder,
      run: activeRun,
    });
  };
  const handleOpenExport = async () => {
    trackMyProjectsExport1();
    const { canOpen, exportAttemptsLimit, exportAttemptsCurrentlyUsed } = await requestExportDialog();
    if (canOpen) {
      setExportAttempts({ exportAttemptsLimit, exportAttemptsCurrentlyUsed });
      setIsExportOpen(true);
    }
  };
  const toggleVisibleConditionSets = () => {
    setVisibleConditionSetCount((currentCount) => {
      const isCollapsing = currentCount >= tableData.length;
      if (isCollapsing) {
        document.getElementById(COLLAPSED_CONDITION_SET_ANCHOR_ID)?.scrollIntoView?.({ block: 'center' });
      }

      return isCollapsing ? CONDITION_SETS_PAGE_SIZE : Math.min(currentCount + CONDITION_SETS_PAGE_SIZE, tableData.length);
    });
  };
  const detailTitle = getDetailTitle(activeRun);

  return (
    <div className="conditions-detail">
      <header className="conditions-detail__header">
        <div className="conditions-detail__context-row">
          <div className="conditions-detail__header-meta">
            <h1 id="conditions-detail-title" className="conditions-detail__title">
              {detailTitle}
            </h1>
            <span className="conditions-detail__source-context">
              <span className="conditions-detail__source-context-label">from</span>
              <ConditionsSourceLink
                run={activeRun}
                reaxysDataE2e="conditions-detail-open-reaxys-source"
                retrosynthesisDataE2e="conditions-detail-open-rt-source"
                linkProps={{ inline: true, navigation: true }}
                onBeforeNavigate={rememberAnchor}
                onBusyChange={setBlockingNavigation}
              />
            </span>
          </div>
          <div className="conditions-detail__header-actions">
            <Button data-e2e="export-condnav" type="secondary" size="small" onClick={handleOpenExport}>
              Export
            </Button>
          </div>
        </div>
        <div className="conditions-detail__metrics">
          {headerMetrics.map(({ key, label, value, modifier, tooltip }) => (
            <Card key={key} className="rx-metric-card" size="small" borderWeight="light">
              <div className="o-els-flex-layout">
                <dl className={`conditions-detail__metric conditions-detail__metric--${modifier}`}>
                  <dt className="conditions-detail__metric-label">{label}</dt>
                  <dd className="conditions-detail__metric-value u-els-font-size-h4">{value}</dd>
                </dl>
                {tooltip && (
                  <ConditionsInfoTooltip ariaLabel={`${label} information`} dataE2e={`conditions-metric-${key}-tooltip`}>
                    {tooltip}
                  </ConditionsInfoTooltip>
                )}
              </div>
            </Card>
          ))}
        </div>
      </header>

      {isExportOpen && (
        <ConditionsExportModal
          onClose={() => setIsExportOpen(false)}
          onExport={handleExport}
          isBusy={isExportBusy}
          exportAttempts={exportAttempts}
        />
      )}

      <div className="conditions-detail__layout">
        <main className="conditions-detail__main" id="main-content" aria-labelledby="conditions-detail-title">
          <section className="conditions-detail__query-reaction-section" aria-label="Query reaction">
            <TogglePanelCollection
              id="conditions-query-reaction-accordion"
              className="conditions-detail__query-reaction-accordion"
              headerLevel="2"
              iconRight
              data-e2e="conditions-query-reaction-accordion"
              initialOpenPanels={['conditions-query-reaction']}>
              <TogglePanel id="conditions-query-reaction" header="Query reaction" data-e2e="conditions-query-reaction-panel">
                <div className="conditions-detail__reaction-placeholder" aria-label="Query reaction structure">
                  {renderReactionPreview()}
                </div>
              </TogglePanel>
            </TogglePanelCollection>
          </section>

          <section className="conditions-detail__section u-els-margin-top-2x" aria-labelledby="rx-condition-sets-heading">
            <h2 id="rx-condition-sets-heading" className="conditions-detail__section-title">
              Similar condition sets
            </h2>
            <div
              id={CONDITION_SETS_TABLE_ID}
              className="conditions-detail__table-wrapper conditions-detail__table-wrapper--animated similar-condition-sets__wrapper">
              <Table
                key={`conditions-detail-table-${id}`}
                data={visibleTableData}
                columns={CONDITIONS_DETAIL_COLUMNS}
                rowKey="rank"
                highlightHeader
                verticalAlignment="middle"
                subtleDividers
                initialSortBy={detailSortBy}
                initialSortOrder={detailSortOrder}
                customSortFunction={conditionsSortFn}
                sortableColumns={SORTABLE_COLUMNS}
                renderHead={(rowProps, sortingProps) => (
                  <ConditionsDetailsTableHeader
                    rowProps={rowProps}
                    sortingProps={{
                      ...sortingProps,
                      isSortable: true,
                      sortBy: detailSortBy,
                      sortOrder: detailSortOrder,
                      sortableColumns: SORTABLE_COLUMNS,
                      updateSortBy: () => null,
                    }}
                    onSortChange={handleDetailSortChange}
                  />
                )}
                a11y={{
                  name: 'Similar condition sets',
                  idForDescription: 'rx-condition-sets-heading',
                }}
                className="conditions-detail__table similar-condition-sets rs-table"
                renderDatum={(sortedData) =>
                  sortedData.map((row, rowIndex) => (
                    <ConditionsDetailsTableRow
                      key={row.rank}
                      row={row}
                      rowIndex={rowIndex}
                      rowId={rowIndex === CONDITION_SETS_PAGE_SIZE - 1 ? COLLAPSED_CONDITION_SET_ANCHOR_ID : undefined}
                      openExamples={openExamples}
                      openFunctionalGroupReactions={openFunctionalGroupReactions}
                      onBeforeNavigate={rememberAnchorAndBlockNavigation}
                    />
                  ))
                }
              />
            </div>
            <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
              {conditionSetStatusMessage}
            </div>
            {hasConditionSetToggle && (
              <div className="conditions-detail__show-more">
                <Button
                  data-e2e="conditions-show-more"
                  type="secondary"
                  aria-controls={CONDITION_SETS_TABLE_ID}
                  onClick={toggleVisibleConditionSets}>
                  {conditionSetToggleLabel}
                </Button>
              </div>
            )}
          </section>

          <section className="conditions-detail__summary-section" aria-label="Condition summary">
            <TogglePanelCollection
              id="conditions-summary-accordion"
              className="conditions-detail__summary-accordion"
              headerLevel="2"
              iconRight
              data-e2e="conditions-summary-accordion"
              initialOpenPanels={['condition-summary']}>
              <TogglePanel
                id="condition-summary"
                header="Condition summary"
                data-e2e="conditions-summary-panel"
                onClick={(panelId, isOpen) => {
                  if (!isOpen) {
                    trackSummaryHide();
                  }
                }}>
                <div id="condition-summary-body" className="conditions-detail__summary-strip">
                  {CHART_CONFIGS.map((chart) => (
                    <Card
                      key={chart.key}
                      className="conditions-detail__chart-panel"
                      heading={<SummaryCardHeading chart={chart} />}
                      headingLevel="h3"
                      size="small"
                      borderWeight="light">
                      {chart.hasTabs ? (
                        <ConditionsSummaryTabGroup chart={chart} summary={summary} onBeforeNavigate={() => setBlockingNavigation(true)} />
                      ) : null}
                    </Card>
                  ))}
                </div>
              </TogglePanel>
            </TogglePanelCollection>
          </section>
          <Footer />
        </main>
      </div>
    </div>
  );
}

ConditionsDetail.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({ id: PropTypes.string }),
  }),
  run: PropTypes.shape({
    id: idPropType,
    projectName: PropTypes.string,
    referenceLabel: PropTypes.string,
  }),
  recommendations: PropTypes.arrayOf(PropTypes.shape({})),
  summary: PropTypes.shape({}),
  isLoading: PropTypes.bool,
  detailHasSettled: PropTypes.bool,
  error: PropTypes.string,
  detailSortBy: PropTypes.string,
  detailSortOrder: PropTypes.string,
  detailRestoration: PropTypes.shape({
    focusTargetId: PropTypes.string,
  }),
  databaseId: idPropType,
  fetchProject: PropTypes.func,
  setDetailSort: PropTypes.func,
  setDetailRestoration: PropTypes.func,
  consumeDetailRestoration: PropTypes.func,
  exportProject: PropTypes.func,
  requestExportDialog: PropTypes.func,
  isExportBusy: PropTypes.bool,
  onProjectLoaded: PropTypes.func,
  setBlockingNavigation: PropTypes.func,
};

export default ConditionsDetail;
